/* RESPONSIBLE TEAM: team-inbox-management */
import { type RenderableData } from 'embercom/objects/inbox/renderable-data';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import { EntityType } from 'embercom/models/data/entity-types';
import UserSummary, { type UserSummaryWireFormat } from '../user-summary';
import AdminSummary, { type AdminSummaryWireFormat } from '../admin-summary';
import { type WorkflowObject } from '../interfaces/workflow';
import { type DataType } from '../conversation-attribute-descriptor';

interface ConversationAttributeSummaryWireFormat {
  id: number;
  name: string;
  is_built_in: boolean;
  type: DataType;
  is_classification_attribute: boolean;
}

export class ConversationAttributeSummary {
  readonly id: number | string;
  readonly name: string;
  readonly isBuiltIn: boolean;
  readonly type: DataType;
  readonly isClassificationAttribute: boolean;

  constructor(
    id: number | string,
    name: string,
    isBuiltIn: boolean,
    type: DataType,
    isClassificationAttribute: boolean,
  ) {
    this.id = id;
    this.name = name;
    this.isBuiltIn = isBuiltIn;
    this.type = type;
    this.isClassificationAttribute = isClassificationAttribute;
  }

  static deserialize(json: ConversationAttributeSummaryWireFormat): ConversationAttributeSummary {
    return new ConversationAttributeSummary(
      json.id,
      json.name,
      json.is_built_in,
      json.type,
      json.is_classification_attribute,
    );
  }
}

interface ConversationAttributeUpdatedWireFormat {
  renderable_type: RenderableType;
  entity_type: EntityType;
  entity: UserSummaryWireFormat | AdminSummaryWireFormat | WorkflowObject;
  attribute_summary: ConversationAttributeSummaryWireFormat;
  value: any | undefined;
  is_classification_attribute: boolean;
  reason?: string;
}

export default class ConversationAttributeUpdated implements RenderableData {
  readonly renderableType: RenderableType = RenderableType.ConversationAttributeUpdated;
  readonly attributeSummary: ConversationAttributeSummary;
  readonly value: any | undefined;
  readonly entity: UserSummary | AdminSummary | WorkflowObject | undefined;
  readonly entityType: EntityType;
  readonly isClassificationAttribute: boolean;
  readonly reason: string | undefined;

  get creatingEntity() {
    return { type: this.entityType, id: this.entity?.id || -1, object: this.entity };
  }

  get type(): DataType {
    return this.attributeSummary.type;
  }

  constructor(
    entity: UserSummary | AdminSummary | WorkflowObject | undefined,
    entityType: EntityType,
    attributeSummary: ConversationAttributeSummary,
    value: any,
    isClassificationAttribute = false,
    reason?: string,
  ) {
    this.entity = entity;
    this.entityType = entityType;
    this.attributeSummary = attributeSummary;
    this.value = value;
    this.isClassificationAttribute = isClassificationAttribute;
    this.reason = reason;
  }

  static deserialize(json: ConversationAttributeUpdatedWireFormat): ConversationAttributeUpdated {
    let entity = undefined;
    switch (json.entity_type) {
      case EntityType.Admin: {
        entity = AdminSummary.deserialize(json.entity as AdminSummaryWireFormat);
        break;
      }
      case EntityType.User: {
        entity = UserSummary.deserialize(json.entity as UserSummary);
        break;
      }
      case EntityType.Workflow: {
        entity = json.entity as WorkflowObject;
        break;
      }
      case EntityType.Bot: {
        entity = AdminSummary.deserialize(json.entity as AdminSummaryWireFormat);
        break;
      }
    }

    let summary = ConversationAttributeSummary.deserialize(json.attribute_summary);
    return new ConversationAttributeUpdated(
      entity,
      json.entity_type,
      ConversationAttributeSummary.deserialize(json.attribute_summary),
      json.value,
      summary.isClassificationAttribute,
      json.reason,
    );
  }

  get adminSummary() {
    if (this.entityType === EntityType.Admin) {
      return this.entity as AdminSummary;
    }
    return undefined;
  }
}
